import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BadgePercent, Check, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  catalogPlanPrice,
  isAnnualOfferedToTenant,
  tenantPlanPrice,
} from "@/lib/tenant-plan-pricing";
import { computePartnerBenefitCopy } from "@/lib/partner-benefit-copy";
import type {
  PartnerBenefitStatus,
  TenantCurrentResponse,
  TenantPlanOption,
} from "@/services/authService";

export interface ChangePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: TenantCurrentResponse;
  plans: TenantPlanOption[];
  /** Null when the tenant has no active partner redemption to explain. */
  partnerBenefit: PartnerBenefitStatus | null;
  selectedPlanId: string | null;
  selectedBillingInterval: "monthly" | "annual";
  onPlanChange: (id: string) => void;
  onBillingIntervalChange: (v: "monthly" | "annual") => void;
  onUpdatePlan: () => void | Promise<void>;
  saving: boolean;
  onOpenLiteralEInfo: () => void;
}

export function ChangePlanModal({
  open,
  onOpenChange,
  tenant,
  plans,
  partnerBenefit,
  selectedPlanId,
  selectedBillingInterval,
  onPlanChange,
  onBillingIntervalChange,
  onUpdatePlan,
  saving,
  onOpenLiteralEInfo,
}: ChangePlanModalProps): JSX.Element {
  const { t } = useTranslation();
  const partnerBenefitCopy = partnerBenefit
    ? computePartnerBenefitCopy(t, partnerBenefit)
    : null;
  const hasChange =
    selectedPlanId !== tenant.planId ||
    selectedBillingInterval !== tenant.billingInterval;
  // When the subscription is past_due/lapsed, allow confirming the SAME plan —
  // re-checking-out the current tier is how a declined card gets fixed (dLocal
  // has no update-card API), so the "no change" gate must not block it.
  const hasPaymentIssue =
    tenant.subscriptionState === "past_due" ||
    tenant.subscriptionState === "lapsed";

  // Benefit tenants are billed monthly only (the 80 UI credit accrues per month),
  // so the server withholds tenantPriceAnnual for their billable tiers.
  const annualOffered = isAnnualOfferedToTenant(plans);

  useEffect(() => {
    if (!annualOffered && selectedBillingInterval === "annual") {
      onBillingIntervalChange("monthly");
    }
  }, [annualOffered, selectedBillingInterval, onBillingIntervalChange]);
  // Literal E ($0, ilimitado) is regime-gated — it isn't a self-serve pick from
  // Plan visibility is driven by the server-computed `eligiblePlanIds` (derived
  // from the tenant's fiscal profile: regime + company type). Show a plan iff
  // it's eligible OR it's the tenant's current/grandfathered plan (so their
  // active plan always renders even when it's not self-selectable).
  const visiblePlans = plans.filter(
    (p) => tenant.eligiblePlanIds.includes(p.id) || p.id === tenant.planId,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto bg-[var(--fx-paper)] p-0 dark:bg-[var(--dk-surface)] sm:max-w-4xl"
      >
        <header
          className="border-b border-[var(--fx-ink-100)] px-6 pb-5 pt-7 dark:border-[var(--dk-border)] sm:px-8 sm:pt-8"
        >
          <span
            className="block text-[11px] font-bold uppercase tracking-[0.18em]"
            style={{
              fontFamily: "var(--fx-ff-mono)",
              color: "var(--fx-accent-700)",
            }}
          >
            {t("settings.plans.modalEyebrow", "Planes de Fiscalo")}
          </span>
          <DialogTitle asChild>
            <h2
              className="mt-2 text-[26px] leading-tight tracking-tight"
              style={{
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              {t("settings.plans.modalTitleLead", "Elegí el plan")}{" "}
              <span
                className="italic"
                style={{
                  fontFamily: "var(--fx-ff-serif)",
                  fontWeight: 500,
                  color: "var(--fx-accent-700)",
                }}
              >
                {t("settings.plans.modalTitleAccent", "que mejor te queda.")}
              </span>
            </h2>
          </DialogTitle>
          <p
            className="mt-2 text-sm leading-relaxed"
            style={{ color: "var(--fx-ink-500)" }}
          >
            {t(
              "settings.plans.modalDescription",
              "Los cambios se aplican en el próximo ciclo. Cancelás cuando quieras.",
            )}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span
              className="text-[11px] font-bold uppercase tracking-[0.18em]"
              style={{
                fontFamily: "var(--fx-ff-mono)",
                color: "var(--fx-ink-500)",
              }}
            >
              {t("settings.plans.paymentMethod", "Forma de pago")}
            </span>
            <div
              className="inline-flex rounded-full p-1"
              style={{
                background: "var(--fx-s-raised-hover)",
                border: "1px solid var(--fx-s-hairline)",
              }}
            >
              <button
                type="button"
                onClick={() => onBillingIntervalChange("monthly")}
                className={cn(
                  "rounded-full px-4 py-1.5 text-[13px] font-semibold transition-all",
                  selectedBillingInterval === "monthly"
                    ? "shadow-sm"
                    : "hover:opacity-80",
                )}
                style={
                  selectedBillingInterval === "monthly"
                    ? {
                        background: "var(--fx-s-inverse-bg)",
                        color: "var(--fx-s-inverse-fg)",
                      }
                    : { color: "var(--fx-ink-500)" }
                }
              >
                {t("settings.plans.monthly")}
              </button>
              {annualOffered ? (
              <button
                type="button"
                onClick={() => onBillingIntervalChange("annual")}
                className={cn(
                  "rounded-full px-4 py-1.5 text-[13px] font-semibold transition-all",
                  selectedBillingInterval === "annual"
                    ? "shadow-sm"
                    : "hover:opacity-80",
                )}
                style={
                  selectedBillingInterval === "annual"
                    ? {
                        background: "var(--fx-s-inverse-bg)",
                        color: "var(--fx-s-inverse-fg)",
                      }
                    : { color: "var(--fx-ink-500)" }
                }
              >
                {t("settings.plans.annual")}
              </button>
              ) : null}
            </div>
            {selectedBillingInterval === "annual" ? (
              <span
                className="text-xs"
                style={{ color: "var(--fx-accent-700)" }}
              >
                {t(
                  "settings.plans.annualSaving",
                  "Pagás 10 meses · te ahorrás 2.",
                )}
              </span>
            ) : null}
          </div>
        </header>

        <div className="px-6 py-6 sm:px-8">
          {partnerBenefitCopy ? (
            <div
              className="mb-5 flex items-start gap-3 rounded-2xl p-4"
              style={{ background: "var(--fx-accent-100)" }}
            >
              <span
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: "var(--fx-accent-500)" }}
              >
                <BadgePercent className="h-[17px] w-[17px]" aria-hidden />
              </span>
              <div className="min-w-0">
                <p
                  className="text-[15px] font-bold leading-snug"
                  style={{ color: "var(--fx-accent-700)" }}
                >
                  {t(
                    "partnerBenefit.bannerTitle",
                    "Tenés el beneficio de {{partner}} activo",
                    { partner: partnerBenefit?.partnerName },
                  )}
                </p>
                <p
                  className="mt-0.5 text-[13px] leading-relaxed opacity-85"
                  style={{ color: "var(--fx-accent-700)" }}
                >
                  {partnerBenefitCopy.durationSentence}
                  {partnerBenefitCopy.nextStageSentence
                    ? ` ${partnerBenefitCopy.nextStageSentence}`
                    : ""}
                </p>
              </div>
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
            {visiblePlans.map((plan) => {
              // Tenant prices throughout: an Art. 5 tenant's 80 UI credit is
              // already deducted, and `isFree` decides free-activation vs
              // checkout. `catalogAmount` is only the struck-through "antes".
              const amount = tenantPlanPrice(plan, selectedBillingInterval);
              const catalogAmount = catalogPlanPrice(
                plan,
                selectedBillingInterval,
              );
              const isDiscounted = amount < catalogAmount;
              const isFree = amount === 0;
              // Neither discount is ever applied annually (both require a
              // monthly cycle), so there's nothing to attribute once annual
              // is selected.
              const isMonthly = selectedBillingInterval === "monthly";
              const hasFiscalBenefit = isMonthly && plan.fiscalBenefitDiscount > 0;
              const hasPartnerBenefit =
                isMonthly && (plan.partnerDiscount ?? 0) > 0 && partnerBenefit != null;
              const periodLabel =
                selectedBillingInterval === "annual"
                  ? t("settings.plans.perYear")
                  : t("settings.plans.perMonth");
              const badge =
                plan.id === "ombu" ? t("settings.plans.ombu.badge") : null;
              const taglineKey = `settings.plans.${plan.id}.tagline`;
              const tagline = t(taglineKey);
              const showTagline = tagline !== "" && tagline !== taglineKey;
              const bullets = t(`settings.plans.${plan.id}.descriptionBullets`, {
                returnObjects: true,
              });
              const hasBullets = Array.isArray(bullets) && bullets.length > 0;
              const isSelected = selectedPlanId === plan.id;
              const isActiveContract = tenant.planId === plan.id;

              return (
                <button
                  type="button"
                  key={plan.id}
                  onClick={() => onPlanChange(plan.id)}
                  aria-pressed={isSelected}
                  className={cn(
                    "group relative flex h-full flex-col rounded-2xl p-5 text-left transition-all duration-200 sm:min-h-[24rem]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  )}
                  style={{
                    background: isSelected
                      ? "var(--fx-s-overlay)"
                      : "var(--fx-s-raised-hover)",
                    border: isSelected
                      ? "2px solid var(--fx-s-inverse-bg)"
                      : "1px solid var(--fx-s-hairline)",
                    boxShadow: isSelected
                      ? "0 12px 32px -16px rgba(22,21,20,0.18)"
                      : "none",
                  }}
                >
                  <div className="flex min-h-[1.5rem] flex-wrap items-center gap-1.5">
                    {badge != null && badge !== "" ? (
                      <Badge
                        className="text-[10px] font-semibold uppercase tracking-wide"
                        style={{
                          background: "var(--fx-accent-500)",
                          color: "var(--fx-s-inverse-fg)",
                        }}
                      >
                        {badge}
                      </Badge>
                    ) : null}
                    {isActiveContract ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-medium"
                        style={{
                          borderColor: "var(--fx-ink-200)",
                          color: "var(--fx-ink-700)",
                        }}
                      >
                        {t("settings.plans.activeBadge")}
                      </Badge>
                    ) : null}
                  </div>

                  <div className="mt-3 space-y-1">
                    <span
                      className="text-[22px] leading-tight"
                      style={{
                        fontFamily: "var(--fx-ff-serif)",
                        fontWeight: 500,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {t(plan.nameKey)}
                    </span>
                    {showTagline ? (
                      <p
                        className="text-xs leading-snug"
                        style={{ color: "var(--fx-ink-500)" }}
                      >
                        {tagline}
                      </p>
                    ) : null}
                    {plan.id === "semilla" ? (
                      <p
                        className="text-[11px] leading-snug"
                        style={{ color: "var(--fx-ink-400)" }}
                      >
                        {t(
                          "settings.plans.semilla.footnote",
                          "Ideal si no aplicás a beneficios fiscales de DGI",
                        )}
                      </p>
                    ) : null}
                  </div>

                  {hasBullets ? (
                    <ul
                      className="mt-4 flex flex-1 flex-col gap-2 text-xs leading-relaxed"
                      style={{ color: "var(--fx-ink-700)" }}
                    >
                      {(bullets as string[]).map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <Check
                            className="mt-0.5 h-3.5 w-3.5 shrink-0"
                            style={{ color: "var(--fx-accent-500)" }}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      className="mt-4 flex-1 text-xs leading-relaxed"
                      style={{ color: "var(--fx-ink-700)" }}
                    >
                      {t(plan.descriptionKey)}
                    </p>
                  )}

                  <div
                    className="mt-4 border-t pt-4"
                    style={{ borderColor: "var(--fx-s-hairline)" }}
                  >
                    {isFree ? (
                      <p
                        className="text-lg font-semibold text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]"
                      >
                        {t("settings.plans.freeLabel")}
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0">
                        {isDiscounted ? (
                          <s
                            className="text-sm font-medium tabular-nums"
                            style={{ color: "var(--fx-ink-500)" }}
                          >
                            ${catalogAmount.toLocaleString("es-UY")}
                          </s>
                        ) : null}
                        <span
                          className="text-2xl font-bold tabular-nums tracking-tight sm:text-3xl"
                          style={{
                            letterSpacing: "-0.025em",
                          }}
                        >
                          ${amount.toLocaleString("es-UY")}
                        </span>
                        <span
                          className="text-sm font-medium"
                          style={{ color: "var(--fx-ink-500)" }}
                        >
                          UYU / {periodLabel}
                        </span>
                        <span
                          className="ml-1 text-[10px] font-medium uppercase tracking-wide"
                          style={{
                            color: "var(--fx-ink-500)",
                            fontFamily: "var(--fx-ff-mono)",
                          }}
                        >
                          {t("settings.plans.plusIva", "+ IVA")}
                        </span>
                      </div>
                    )}
                    {hasFiscalBenefit || hasPartnerBenefit ? (
                      <div className="mt-2 flex flex-col gap-1">
                        {hasPartnerBenefit ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold"
                            style={{ color: "var(--fx-accent-700)" }}
                          >
                            <BadgePercent
                              className="h-3 w-3 shrink-0"
                              aria-hidden
                            />
                            {t(
                              "partnerBenefit.cardTag",
                              "Incluye tu beneficio de {{partner}}",
                              { partner: partnerBenefit?.partnerName },
                            )}
                          </span>
                        ) : null}
                        {hasFiscalBenefit ? (
                          <span
                            className="text-[11px] font-medium"
                            style={{ color: "var(--fx-ink-500)" }}
                          >
                            {t(
                              "settings.plans.fiscalBenefitTag",
                              "Incluye crédito fiscal (80 UI)",
                            )}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          {tenant.eligiblePlanIds.includes("literalE") ? (
            <div
              className="mt-5 rounded-lg px-3 py-2"
              style={{
                border: "1px solid var(--fx-s-hairline)",
                background: "var(--fx-s-raised-hover)",
              }}
            >
              <p className="text-xs" style={{ color: "var(--fx-ink-700)" }}>
                {t("settings.plans.literalEDiscount")}{" "}
                <button
                  type="button"
                  onClick={onOpenLiteralEInfo}
                  className="rounded underline transition-colors hover:no-underline focus:outline-none"
                  style={{ color: "var(--fx-accent-700)" }}
                >
                  {t("settings.plans.literalELearnMore")}
                </button>
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter
          className="border-t px-6 py-4 sm:px-8"
          style={{ borderColor: "var(--fx-s-hairline)" }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="button"
            onClick={() => void onUpdatePlan()}
            disabled={(!hasChange && !hasPaymentIssue) || saving}
            className="min-w-[10rem]"
            style={{
              background: "var(--fx-s-inverse-bg)",
              color: "var(--fx-s-inverse-fg)",
            }}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("settings.plans.updating")}
              </>
            ) : (
              t("settings.plans.modifyButton")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
